import Divider from '@/components/core/divider/Divider';
import EmailLink from '@/components/core/link/EmailLink';
import PhoneLink from '@/components/core/link/PhoneLink';
import PositionLink from '@/components/core/link/PositionLink';
import Logo from '@/components/core/logo/Logo';
import SocialIconMapping from '@/components/utils/socialIconMapping';
import { Footer } from '@/models/interfaces/pages/root';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import React, { Fragment, useState } from 'react';

import fsc from '@/assets/images/loghi-istituzionali/fsc.png';
import regioneCalabria from '@/assets/images/loghi-istituzionali/regione-calabria.png';
import repubblicaItaliana from '@/assets/images/loghi-istituzionali/repubblica-italiana.png';
import unioneEuropea from '@/assets/images/loghi-istituzionali/unione-europea.png';

interface FooterProps {
  footer?: Footer;
}

const Footer = ({ footer }: FooterProps) => {
  const { address, email, links, phone, socials } = footer ?? {};

  const { t } = useTranslation();

  return (
    <footer className="shadow-full relative z-10 ">
      <div className="container pb-2 pt-20">
        <div className="flex flex-col items-center gap-8 text-center lg:grid lg:grid-cols-4 lg:items-start lg:text-left">
          <div className="mb-10 w-40 translate-x-7 lg:translate-x-0">
            <Logo type="standardLockup" to="/" />
          </div>

          <div className="space-y-8">
            {phone?.content ? (
              <div className="flex flex-col items-center space-y-2 text-dark dark:text-white lg:items-start lg:space-y-0">
                <div className="mx-auto flex justify-center text-dark dark:text-white/50 lg:hidden">
                  {/* <PhoneIcon className="w-6 h-6" /> */}
                </div>
                <p className="text-lg text-dark dark:text-white/50">{t(phone?.label ?? '')}</p>
                <PhoneLink href={phone?.content ?? ''} label={phone?.content ?? ''} icon={false} />
              </div>
            ) : null}

            {email?.content ? (
              <div className="flex flex-col items-center space-y-2 text-dark dark:text-white lg:items-start lg:space-y-0">
                <div className="mx-auto flex justify-center text-dark dark:text-white/50 lg:hidden">
                  {/* <MailIcon className="w-5 h-5" /> */}
                </div>
                <p className="text-lg text-dark dark:text-white/50">{t(email?.label ?? '')}</p>

                <div className="flex flex-col space-y-1">
                  <EmailLink href={email?.content ?? ''} label={email?.content ?? ''} icon={false} />
                </div>
              </div>
            ) : null}
          </div>

          {address?.url ? (
            <div className="flex flex-col items-center space-y-2 text-dark dark:text-white lg:items-start lg:space-y-0">
              <div className="mx-auto flex justify-center text-dark dark:text-white/50 lg:hidden">
                {/* <AddressIcon className="w-6 h-6" /> */}
              </div>
              <p className="text-lg text-dark dark:text-white/50">{t(address?.label ?? '')}</p>
              <PositionLink href={address?.url ?? ''} label={address?.full_address ?? ''} icon={false} />
            </div>
          ) : null}

          <div className="flex flex-col items-center justify-center text-dark dark:text-white lg:items-start ">
            <p className="mx-auto text-lg text-dark dark:text-white/50">{t('Social Network')}</p>

            <div className="mx-auto mt-1 flex items-center">
              {socials?.map((social, index) => {
                const Icon = SocialIconMapping[social?.id ?? ''];

                if (!Icon) return null;

                return (
                  <Fragment key={social.id}>
                    <a
                      key={index}
                      href={social?.url ?? ''}
                      target={'_blank'}
                      rel="noreferrer"
                      className="flex items-center gap-1 hover:text-sky-600"
                    >
                      <Icon />
                    </a>

                    {index < socials.length - 1 && (
                      <div className="mx-4 h-4 w-px bg-dark/50 dark:bg-white/50" />
                    )}
                  </Fragment>
                );
              })}
            </div>
          </div>
        </div>

        <Divider className="my-10" classColor="bg-gray-300" />

        <div className="relative mx-auto flex h-[4.2rem] w-fit justify-center gap-3 overflow-hidden rounded-2xl px-4 py-2 text-center dark:bg-white lg:gap-8 lg:px-8 ">
          {/* developed by{' '}
          <a
            href="https://www.altrama.com"
            target={'_blank'}
            rel="noreferrer"
            className="font-bold hover:text-green-500"
          >
            Altrama Italia S.r.l.
          </a> */}

          <div className="relative">
            <Image src={fsc} alt="loghi istituzionali" className="h-full w-full object-contain" />
          </div>

          <div className="relative">
            <Image src={unioneEuropea} alt="loghi istituzionali" className="h-full w-full object-contain" />
          </div>

          <div className="relative">
            <Image
              src={repubblicaItaliana}
              alt="loghi istituzionali"
              className="h-full w-full object-contain"
            />
          </div>

          <div className="relative">
            <Image src={regioneCalabria} alt="loghi istituzionali" className="h-full w-full object-contain" />
          </div>
        </div>

        <div className="mt-10 flex-col gap-8 lg:flex lg:items-center lg:justify-center">
          <div className="flex items-center justify-center text-dark dark:text-white/50 lg:mt-0">
            {links?.map((item) => {
              const isLast = links?.indexOf(item) === links?.length - 1;

              return (
                <Fragment key={item?.title}>
                  <a
                    href={item?.url ?? ''}
                    className="text-sm hover:text-sky-600 dark:text-white dark:hover:text-sky-600"
                    rel="noreferrer"
                  >
                    {t(item?.title ?? '')}
                  </a>

                  {!isLast && <div className="mx-2 h-4 w-px bg-stone-500/50" />}
                </Fragment>
              );
            })}
          </div>

          <p className="mt-10 text-center text-base text-dark dark:text-white/50 lg:mt-0">
            2023© <strong className="text-dark dark:text-white/50"> {t('Regione Calabria')}</strong> -{' '}
            {t('Tutti i diritti riservati')}
          </p>
        </div>
      </div>

      {/* Privacy - Policy */}
      {/* <PrivacyPolicy
        isOpenPrivacyPolicy={isOpenPrivacyPolicy}
        setIsOpenPrivacyPolicy={setIsOpenPrivacyPolicy}
      /> */}

      {/* Cookie */}
      {/* <Cookie isOpenCookie={isOpenCookie} setIsOpenCookie={setIsOpenCookie} /> */}
    </footer>
  );
};

export default Footer;
